<template>
  <Transition name="config-swap" mode="out-in">
    <div
      v-if="!selectedAccount"
      key="grid"
      class="row mt-0 h-100"
      v-motion-fade
    >
      <!-- MAIN CONTENT -->
      <div class="col-md-12 col-lg-12 mb-1">
        <div class="card accounts-list-card">
          <div class="card-header pt-1 pb-1">
            <!-- HEADER -->
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div>
                <h5 class="mb-0">User Accounts</h5>
                <p class="text-sm text-secondary mb-0">
                  {{ accounts?.data?.totalRecords || 0 }} accounts found
                </p>
              </div>
            </div>

            <!-- TOOLBAR -->
            <div
              class="d-flex flex-wrap gap-1 align-items-center accounts-toolbar"
            >
              <el-select
                class="form-control"
                style="max-width: 140px"
                v-model="pageSize"
                placeholder="Page Size"
                @change="
                  pageNumber = 1;
                  getAccounts();
                "
              >
                <el-option
                  v-for="item in pageSizes"
                  :key="item"
                  :label="`${item} records`"
                  :value="item"
                />
              </el-select>

              <el-pagination
                background
                layout="prev, pager, next"
                :current-page="pageNumber"
                :page-size="pageSize"
                :total="accounts?.data?.totalRecords"
                @current-change="
                  pageNumber = $event;
                  getAccounts();
                "
              />

              <div class="input-group ms-auto" style="max-width: 260px">
                <span class="input-group-text">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  v-model="filter"
                  class="form-control form-control-sm"
                  placeholder="Quick search..."
                />
                <argon-button
                  class="btn btn-sm border-0"
                  @click="openNewAccountModal"
                  color="success"
                  size="md"
                  variant="gradient"
                >
                  <i class="fas fa-plus me-1"></i>
                  New
                </argon-button>
              </div>
            </div>
          </div>

          <!-- USERS LIST -->
          <div class="card-body permissions-container">
            <div v-if="isLoading" class="account-loading-state">
              <i class="fas fa-circle-notch fa-spin"></i>
              <p class="mb-0">Loading accounts...</p>
            </div>
            <div
              v-else-if="!accounts?.data?.data?.length"
              class="account-empty-state"
            >
              <i class="fas fa-user-slash"></i>
              <p class="mb-0">No accounts found</p>
            </div>
            <div v-else class="row g-2">
              <div
                v-for="item in accounts?.data?.data"
                :key="item.id"
                class="col-12 col-sm-6 col-md-6 col-lg-3"
              >
                <div class="account-card p-3">
                  <div class="d-flex align-items-start gap-3">
                    <div
                      :class="[
                        'account-avatar',
                        `account-avatar--${avatarColorIndex(item.userName)}`,
                      ]"
                    >
                      {{ initials(item.userName) }}
                    </div>
                    <div class="flex-grow-1" style="min-width: 0">
                      <h6 class="mb-1 text-truncate">{{ item.userName }}</h6>
                      <span
                        class="account-status-badge"
                        :class="item.active == 1 ? 'is-active' : 'is-active'"
                      >
                        {{ item.active == 0 ? "Active" : "Active" }}
                      </span>
                    </div>
                    <button
                      type="button"
                      class="account-card-action-btn"
                      title="Configure"
                      @click="getUserConfigurationsById(item)"
                    >
                      <i class="fas fa-cog"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <account-config-panel
      v-else
      key="config"
      v-motion-slide-right
      :account="selectedAccount"
      :roles="roles"
      :user-fields="userFields"
      :user-i-d="userID"
      :field-values="fieldValues"
      :permissions="permissionsState?.data || []"
      :permissions-total-records="permissionsState?.totalRecords || 0"
      :permissions-page-size="permissionPageSize"
      :permissions-page-number="permissionPageNumber"
      :permissions-filter="permissionFilter"
      :assigned-permission-ids="assignedPermissionIds"
      :permissions-loading="permissionsLoading"
      @close="closeConfigPanel"
      @update-field-status="updateUserFieldStatus"
      @update-role-status="updateUserRoleStatus"
      @field-input="handleFieldInput"
      @view-password="showPassword"
      @reset-password="showResetPasswordForm"
      @toggle-permission="togglePermission"
      @update-permissions-filter="updatePermissionFilter"
      @update-permissions-page-size="updatePermissionPageSize"
      @update-permissions-page="updatePermissionPage"
    />
  </Transition>
</template>

<script>
import setNavPills from "@/assets/js/nav-pills.js";
import setTooltip from "@/assets/js/tooltip.js";
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { /*ElTable, ElTableColumn,*/ ElPagination } from "element-plus";
import Swal from "sweetalert2";
import { useStore } from "vuex";
import ArgonButton from "@/components/ArgonButton.vue";
import AccountConfigPanel from "./AccountConfigPanel.vue";
import { showSuccessToast, showinfoToast, showAlert } from "@/utils/alerts";

const AVATAR_COLOR_COUNT = 8;

const avatarColorIndex = (username) => {
  if (!username) return 0;
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = (hash * 31 + username.charCodeAt(i)) % AVATAR_COLOR_COUNT;
  }
  return hash;
};

const initials = (username) => {
  if (!username) return "?";
  return username.slice(0, 2).toUpperCase();
};

export default defineComponent({
  name: "accounts-table",
  components: {
    ElPagination,
    ArgonButton,
    AccountConfigPanel,
  },
  props: {
    columnDefs: {
      type: Object,
      required: true,
    },
  },

  setup(__, { emit }) {
    const store = useStore();
    const filter = ref("");
    const pageSize = ref(20);
    const pageNumber = ref(1);
    const pageSizes = ref([10, 20, 50, 100]);
    const roles = ref([]);
    const userFields = ref([]);
    const userID = ref(-1);
    const typoConfig = ref(-1);
    const fieldValues = ref({});
    const timer = ref();
    const selectedAccount = ref(null);
    const isLoading = ref(false);
    const permissionFilter = ref("");
    const permissionPageSize = ref(10);
    const permissionPageNumber = ref(1);
    const assignedPermissionIds = ref([]);

    onMounted(async () => {
      try {
        setTooltip();
        await Promise.all([getAccounts(), getAllRoles(), getAllUserFields()]);
      } catch (error) {
        console.error("Error during onMounted initialization:", error);
      }
    });

    let navPillsSet = false;

    const accounts = computed(() => store.state.accounts.accountList);
    const permissionsState = computed(
      () => store.state.permissions.permissionList,
    );
    const permissionsLoading = computed(() => store.state.permissions.fetching);

    const getAccounts = async () => {
      isLoading.value = true;
      try {
        let params = `?pageSize=${pageSize.value}`;
        params += `&pageNumber=${pageNumber.value}`;
        params += `&includeRoles=true`;
        params += `&includeUserFields=true`;
        params += `&includePermissions=true`;
        if (filter.value != "")
          params += `&filter=x.username.contains("${filter.value}") or x.email.contains("${filter.value}") or x.phone.contains("${filter.value}")`;
        await store.dispatch("accounts/getAccounts", params);
      } finally {
        isLoading.value = false;
      }
    };

    const getAllRoles = async () => {
      await store.dispatch("roles/getAllRoles", "?includePermissions=true");
    };

    const getAllUserFields = async () => {
      await store.dispatch("fields/getAllUserFields");
    };

    const getAllPermissions = async () => {
      let params = `?pageSize=${permissionPageSize.value}`;
      params += `&pageNumber=${permissionPageNumber.value}`;
      if (permissionFilter.value != "")
        params += `&filter=x.name.contains("${permissionFilter.value}") or x.description.contains("${permissionFilter.value}")`;
      await store.dispatch("permissions/getPermissions", params);
    };

    const getUserConfigurationsById = async (item) => {
      selectedAccount.value = item;

      await getRolesByUserId(item.id);
      await getUserFieldsByUserId(item.id);
      assignedPermissionIds.value = store.getters[
        "accounts/getAccountPermissionIds"
      ](item.id);
      permissionFilter.value = "";
      permissionPageNumber.value = 1;
      await getAllPermissions();

      if (!navPillsSet) {
        setNavPills();
        navPillsSet = true; // Marcar como ejecutado
      }
    };

    const closeConfigPanel = () => {
      selectedAccount.value = null;
      userID.value = -1;
      assignedPermissionIds.value = [];
      permissionFilter.value = "";
      permissionPageNumber.value = 1;
    };

    const handleFieldInput = ({ event, field }) => {
      handleInput(event, field);
    };

    const togglePermission = async ({ id, enabled }) => {
      const userPermission = {
        userId: userID.value,
        permissionsId: [id],
      };

      if (enabled) {
        await store.dispatch("accounts/addPermissionsToUser", userPermission);
        showSuccessToast("Permission has been added");
        if (!assignedPermissionIds.value.includes(id)) {
          assignedPermissionIds.value.push(id);
        }
      } else {
        await store.dispatch(
          "accounts/removePermissionsToUser",
          userPermission,
        );
        showinfoToast("Permission has been removed");
        assignedPermissionIds.value = assignedPermissionIds.value.filter(
          (pid) => pid !== id,
        );
      }
    };

    const updatePermissionFilter = (value) => {
      permissionFilter.value = value;
      permissionPageNumber.value = 1;
      getAllPermissions();
    };

    const updatePermissionPageSize = (value) => {
      permissionPageSize.value = value;
      permissionPageNumber.value = 1;
      getAllPermissions();
    };

    const updatePermissionPage = (value) => {
      permissionPageNumber.value = value;
      getAllPermissions();
    };

    const showPassword = async (item) => {
      const payload = { encriptedPassword: item.password };
      await store.dispatch("accounts/getPasswd", payload);
      let plainPassword = store.getters["accounts/getPlainPassword"]();
      showAlert(`The Password for ${item.userName} is`, plainPassword, "info");
    };

    const showResetPasswordForm = async (item) => {
      const { value: formValues } = await Swal.fire({
        title: `Reset Password for ${item.userName}`,
        html:
          `<input id="swal-old-password" type="password" class="swal2-input" placeholder="Old Password">` +
          `<input id="swal-new-password" type="password" class="swal2-input" placeholder="New Password">` +
          `<input id="swal-confirm-password" type="password" class="swal2-input" placeholder="Confirm Password">`,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Save",
        cancelButtonText: "Cancel",
        preConfirm: () => {
          const oldPassword =
            document.getElementById("swal-old-password").value;
          const newPassword =
            document.getElementById("swal-new-password").value;
          const confirmPassword = document.getElementById(
            "swal-confirm-password",
          ).value;

          if (!newPassword || !confirmPassword) {
            Swal.showValidationMessage("Both fields are required");
            return false;
          }
          if (newPassword !== confirmPassword) {
            Swal.showValidationMessage("Passwords do not match");
            return false;
          }
          return { oldPassword, newPassword };
        },
      });

      if (formValues) {
        let payload = {
          username: item.userName,
          oldPassword: formValues.oldPassword,
          newPassword: formValues.newPassword,
        };
        await store.dispatch("accounts/updatePasswd", payload);
        if (store.getters["accounts/isTransactionSuccess"]) {
          Swal.fire("Updated!", "Password is updated successfuly", "success");
          getAccounts();
        }
      }
    };

    const getRolesByUserId = async (userId) => {
      roles.value = store.getters["roles/getAllRoles"];
      typoConfig.value = 1;
      userID.value = userId;
      let ids = store.getters["accounts/getAccountRoles"](userId);
      for (const obj of roles.value) {
        obj.isActive = ids.includes(obj.id);
      }
    };

    const getUserFieldsByUserId = async (userId) => {
      userFields.value = store.getters["fields/getAllUserFields"];
      typoConfig.value = 2;
      userID.value = userId;
      let accountFields = store.getters["accounts/getAccountFields"](userId);
      let ids = accountFields.map((obj) => obj.id);
      userFields.value.forEach((obj) => {
        obj.isActive = ids.includes(obj.id);
        obj.isLoading = false;
        fieldValues.value[obj.id] = obj.isActive
          ? accountFields.find((element) => element.id === obj.id).value
          : "";
      });
    };

    const handleInput = ($event, field) => {
      if (timer.value) {
        clearTimeout(timer.value);
      }
      timer.value = setTimeout(() => {
        updateFieldValue($event.target.value, field.id, field);
      }, 500);
    };

    const updateFieldValue = async (value, fieldId, field) => {
      field.isLoading = true;
      const fieldValue = {
        fieldId: fieldId,
        userId: userID.value,
        value: value,
      };
      await store.dispatch("fields/updateFieldValue", fieldValue);
      if (store.getters["fields/isTransactionSuccess"]) {
        field.isLoading = false;
      }
    };

    const updateUserRoleStatus = async (role) => {
      const userRoles = {
        userId: userID.value,
        roleIds: [role.id],
      };

      if (role.isActive) {
        await store.dispatch("roles/AddRolesToUser", userRoles);
        showSuccessToast("Role has been added");
      } else {
        await store.dispatch("roles/RemoveRolesToUser", userRoles);
        showinfoToast("Role has been removed");
      }
    };

    const updateUserFieldStatus = async (field) => {
      const userFields = {
        userId: userID.value,
        fieldIds: [field.id],
      };

      if (field.isActive) {
        await store.dispatch("fields/AddFieldsToUser", userFields);
        showSuccessToast("Field has been added");
      } else {
        await store.dispatch("fields/RemoveFieldsToUser", userFields);
        showinfoToast("Field has been removed");
      }
    };

    const openNewAccountModal = () => {
      emit("open-modal");
    };

    watch(filter, () => {
      getAccounts();
    });

    return {
      userID,
      accounts,
      roles,
      userFields,
      filter,
      pageSize,
      pageSizes,
      pageNumber,
      typoConfig,
      fieldValues,
      selectedAccount,
      isLoading,
      permissionFilter,
      permissionPageSize,
      permissionPageNumber,
      assignedPermissionIds,
      permissionsState,
      permissionsLoading,
      initials,
      avatarColorIndex,
      getAccounts,
      getAllRoles,
      showPassword,
      getUserConfigurationsById,
      closeConfigPanel,
      handleFieldInput,
      togglePermission,
      updatePermissionFilter,
      updatePermissionPageSize,
      updatePermissionPage,
      updateUserRoleStatus,
      updateUserFieldStatus,
      handleInput,
      openNewAccountModal,
      showResetPasswordForm,
    };
  },
});
</script>

<style scoped>
.permissions-container {
  overflow-y: auto;
}
</style>
